/**
 * DripWelcomeBanner — shared visual for every drip email landing.
 * =========================================================================
 *
 * Wraps a caregiver-facing "you came from Lane's email" section with:
 *   - Eyebrow label ("From Lane's email")
 *   - Personalised message using {lovedOneName}
 *   - Dismiss X (calls the `onDismiss` prop)
 *   - Optional children (the page-specific quick-add strip, appointment
 *     prep chips, burnout mini-slider, etc.)
 *
 * Copy is provided by the parent so each page can speak in its own voice
 * (behavior tracker's "one tap" promise sounds different from the
 * burnout check-in's "how are YOU holding up").
 */
import React from "react";
import { Sparkles, X } from "lucide-react";

export default function DripWelcomeBanner({
    kind,
    message,
    eyebrow = "From Lane's email",
    onDismiss,
    testid = "drip-welcome-banner",
    children,
}) {
    return (
        <section
            data-testid={testid}
            data-drip-kind={kind || ""}
            className="ml-card p-5 md:p-6 border-l-4 border-l-teal-dark bg-gradient-to-br from-teal-soft/70 via-cream-light to-yellow-soft/30 relative"
        >
            <div className="flex items-start gap-3">
                <span className="shrink-0 w-11 h-11 rounded-2xl bg-white flex items-center justify-center">
                    <Sparkles size={18} strokeWidth={1.8} className="text-teal-dark" />
                </span>
                <div className="flex-1 min-w-0 pr-8">
                    <p className="text-[11px] uppercase tracking-wider font-semibold text-teal-dark">
                        {eyebrow}
                    </p>
                    <p
                        data-testid={`${testid}-text`}
                        className="mt-1 text-[17px] md:text-lg text-[#1F2624] leading-relaxed"
                    >
                        {message}
                    </p>
                </div>
                <button
                    type="button"
                    aria-label="Dismiss"
                    data-testid={`${testid}-close`}
                    onClick={onDismiss}
                    className="absolute top-4 right-4 w-8 h-8 rounded-full hover:bg-white/60 flex items-center justify-center text-[#4C5551]"
                >
                    <X size={16} strokeWidth={1.8} />
                </button>
            </div>
            {children ? <div className="mt-5">{children}</div> : null}
        </section>
    );
}
